import * as yup from "yup";
import { CompanySignUpDTO, Country, Language, LegalForm } from "@/api/data-contracts";
import { useMemo } from "react";
import {
    countryOptions,
    languageOptions,
    legalFormOptions,
    salutationOptions,
    useLegalFormsByCountry
} from "@/datacaches";
import { allowedLegalForms } from "@/model/legalForm";
import { Constants } from "@/api/constants";
import {
    BIC_REGEX,
    COMMERCIAL_REGISTRATION_NUMBER_REGEX,
    EMAIL_REGEX,
    IBAN_REGEX,
    PASSWORD_REGEX,
    PHONE_NUMBER_REGEX,
    URL_REGEX,
    VAT_ID_REGEX,
    ZIP_CODE_REGEX
} from "@/regex";
import { AuthI18N, CompanyI18N, UserI18N } from "@/translations";
import { useTranslation } from "react-i18next";

/**
 * Builds the company sign-up schema. `availableLanguages` restricts the `language` field's
 * `.oneOf(...)` validation to languages with a maintained/uploaded language file (see
 * `useAvailableLanguages` in `datacaches.ts`) — defaults to the static `languageOptions`
 * (baseline: GERMAN only) so existing callers without the dynamic set keep working.
 * An empty `availableLanguages` (the cache's loading/error state) falls back to the same static
 * `languageOptions` baseline internally, so `.oneOf(...)` never validates against an empty set.
 */
export const useSignUpSchema = (availableLanguages: Language[] = languageOptions) => {
    const { t } = useTranslation();
    const [legalFormsByCountry] = useLegalFormsByCountry();
    return useMemo(() => {
        // `useAvailableLanguages()` resolves to `[]` while loading and permanently after a
        // fetch error. Fall back to the static baseline so `.oneOf(...)` never validates against
        // an empty set (same rationale as the invitation schemas).
        const languagesForValidation = availableLanguages.length > 0 ? availableLanguages : languageOptions;
        return yup.object().shape({
            email: yup
                .string()
                .required(t(UserI18N.form.validation.emailRequired))
                .min(
                    Constants.EMAIL_LENGTH_MIN,
                    t(UserI18N.form.validation.emailMin, { min: Constants.EMAIL_LENGTH_MIN })
                )
                .max(
                    Constants.EMAIL_LENGTH_MAX,
                    t(UserI18N.form.validation.emailMax, { max: Constants.EMAIL_LENGTH_MAX })
                )
                .matches(EMAIL_REGEX, t(UserI18N.form.validation.emailInvalid)),

            password: yup
                .string()
                .required(t(UserI18N.form.validation.passwordRequired))
                .min(
                    Constants.PASSWORD_LENGTH_MIN,
                    t(UserI18N.form.validation.passwordMin, { min: Constants.PASSWORD_LENGTH_MIN })
                )
                .max(
                    Constants.PASSWORD_LENGTH_MAX,
                    t(UserI18N.form.validation.passwordMax, { max: Constants.PASSWORD_LENGTH_MAX })
                )
                .matches(PASSWORD_REGEX, t(UserI18N.form.validation.passwordInvalid)),

            confirmPassword: yup
                .string()
                .required(t(UserI18N.form.validation.passwordRequired))
                .min(
                    Constants.PASSWORD_LENGTH_MIN,
                    t(UserI18N.form.validation.passwordMin, { min: Constants.PASSWORD_LENGTH_MIN })
                )
                .max(
                    Constants.PASSWORD_LENGTH_MAX,
                    t(UserI18N.form.validation.passwordMax, { max: Constants.PASSWORD_LENGTH_MAX })
                )
                .oneOf([yup.ref("password")], t(UserI18N.form.validation.passwordsMustMatch)),

            salutation: yup
                .string()
                .required(t(UserI18N.form.validation.salutationRequired))
                .oneOf(salutationOptions, t(UserI18N.form.validation.salutationInvalid)),

            firstName: yup
                .string()
                .required(t(UserI18N.form.validation.firstNameRequired))
                .min(
                    Constants.FIRSTNAME_LENGTH_MIN,
                    t(UserI18N.form.validation.firstNameMin, { min: Constants.FIRSTNAME_LENGTH_MIN })
                )
                .max(
                    Constants.FIRSTNAME_LENGTH_MAX,
                    t(UserI18N.form.validation.firstNameMax, { max: Constants.FIRSTNAME_LENGTH_MAX })
                ),

            lastName: yup
                .string()
                .required(t(UserI18N.form.validation.lastNameRequired))
                .min(
                    Constants.LASTNAME_LENGTH_MIN,
                    t(UserI18N.form.validation.lastNameMin, { min: Constants.LASTNAME_LENGTH_MIN })
                )
                .max(
                    Constants.LASTNAME_LENGTH_MAX,
                    t(UserI18N.form.validation.lastNameMax, { max: Constants.LASTNAME_LENGTH_MAX })
                ),

            phone: yup
                .string()
                .required(t(UserI18N.form.validation.phoneRequired))
                .min(
                    Constants.PHONE_NUMBER_LENGTH_MIN,
                    t(UserI18N.form.validation.phoneMin, { min: Constants.PHONE_NUMBER_LENGTH_MIN })
                )
                .max(
                    Constants.PHONE_NUMBER_LENGTH_MAX,
                    t(UserI18N.form.validation.phoneMax, { max: Constants.PHONE_NUMBER_LENGTH_MAX })
                )
                .matches(PHONE_NUMBER_REGEX, t(UserI18N.form.validation.phoneInvalid)),

            fax: yup
                .string()
                .optional()
                .nullable()
                .min(
                    Constants.PHONE_NUMBER_LENGTH_MIN,
                    t(UserI18N.form.validation.faxMin, { min: Constants.PHONE_NUMBER_LENGTH_MIN })
                )
                .max(
                    Constants.PHONE_NUMBER_LENGTH_MAX,
                    t(UserI18N.form.validation.faxMax, { max: Constants.PHONE_NUMBER_LENGTH_MAX })
                )
                .matches(PHONE_NUMBER_REGEX, t(UserI18N.form.validation.faxInvalid)),

            language: yup.string().oneOf(languagesForValidation, t(UserI18N.form.validation.languageRequired)),

            ceo: yup
                .boolean()
                .required(t(UserI18N.form.validation.isCEORequired))
                .oneOf([true], t(UserI18N.form.validation.mustBeCEO)),

            companyName: yup
                .string()
                .required(t(CompanyI18N.form.validation.nameRequired))
                .min(
                    Constants.COMPANY_NAME_LENGTH_MIN,
                    t(CompanyI18N.form.validation.nameMin, { min: Constants.COMPANY_NAME_LENGTH_MIN })
                )
                .max(
                    Constants.COMPANY_NAME_LENGTH_MAX,
                    t(CompanyI18N.form.validation.nameMax, { max: Constants.COMPANY_NAME_LENGTH_MAX })
                ),

            country: yup
                .string()
                .required(t(CompanyI18N.form.validation.countryRequired))
                .oneOf(countryOptions, t(CompanyI18N.form.validation.countryInvalid)),

            legalForm: yup
                .string()
                .required(t(CompanyI18N.form.validation.legalFormRequired))
                .oneOf(legalFormOptions, t(CompanyI18N.form.validation.legalFormInvalid))
                .test(
                    "legal-form-matches-country",
                    t(CompanyI18N.form.validation.legalFormForCountryInvalid),
                    function (value) {
                        // Required-ness is enforced separately above — an empty value is not this test's concern.
                        if (!value) {
                            return true;
                        }
                        return allowedLegalForms(this.parent.country, legalFormsByCountry).includes(value as LegalForm);
                    }
                ),

            streetAddress: yup
                .string()
                .required(t(CompanyI18N.form.validation.streetRequired))
                .min(
                    Constants.STREET_LENGTH_MIN,
                    t(CompanyI18N.form.validation.streetMin, { min: Constants.STREET_LENGTH_MIN })
                )
                .max(
                    Constants.STREET_LENGTH_MAX,
                    t(CompanyI18N.form.validation.streetMax, { max: Constants.STREET_LENGTH_MAX })
                ),
            houseNumber: yup
                .string()
                .required(t(CompanyI18N.form.validation.houseNumberRequired))
                .min(
                    Constants.HOUSE_NUMBER_LENGTH_MIN,
                    t(CompanyI18N.form.validation.houseNumberMin, { min: Constants.HOUSE_NUMBER_LENGTH_MIN })
                )
                .max(
                    Constants.HOUSE_NUMBER_LENGTH_MAX,
                    t(CompanyI18N.form.validation.houseNumberMax, { max: Constants.HOUSE_NUMBER_LENGTH_MAX })
                ),

            postalCode: yup
                .string()
                .required(t(CompanyI18N.form.validation.postalCodeRequired))
                .min(
                    Constants.ZIP_LENGTH_MIN,
                    t(CompanyI18N.form.validation.postalCodeMin, { min: Constants.ZIP_LENGTH_MIN })
                )
                .max(
                    Constants.ZIP_LENGTH_MAX,
                    t(CompanyI18N.form.validation.postalCodeMax, { max: Constants.ZIP_LENGTH_MAX })
                )
                .matches(ZIP_CODE_REGEX, t(CompanyI18N.form.validation.postalCodeInvalid)),

            city: yup
                .string()
                .required(t(CompanyI18N.form.validation.cityRequired))
                .min(
                    Constants.CITY_LENGTH_MIN,
                    t(CompanyI18N.form.validation.cityMin, { min: Constants.CITY_LENGTH_MIN })
                )
                .max(
                    Constants.CITY_LENGTH_MAX,
                    t(CompanyI18N.form.validation.cityMax, { max: Constants.CITY_LENGTH_MAX })
                ),

            website: yup
                .string()
                .optional()
                .nullable()
                .transform((value) => value || null)
                .min(
                    Constants.WEBSITE_LENGTH_MIN,
                    t(CompanyI18N.form.validation.websiteMin, { min: Constants.WEBSITE_LENGTH_MIN })
                )
                .max(
                    Constants.WEBSITE_LENGTH_MAX,
                    t(CompanyI18N.form.validation.websiteMax, { max: Constants.WEBSITE_LENGTH_MAX })
                )
                .matches(URL_REGEX, t(CompanyI18N.form.validation.websiteInvalid)),

            commercialRegisterNumber: yup
                .string()
                .when("country", {
                    is: (country: Country) => country === Country.GERMANY,
                    then: (schema) => schema.required(t(CompanyI18N.form.validation.commercialRegisterNumberRequired)),
                    otherwise: (schema) => schema.optional().nullable()
                })
                .transform((value) => value || null)
                .min(
                    Constants.COMMERCIAL_REGISTER_NUMBER_LENGTH_MIN,
                    t(CompanyI18N.form.validation.commercialRegisterNumberMin, {
                        min: Constants.COMMERCIAL_REGISTER_NUMBER_LENGTH_MIN
                    })
                )
                .max(
                    Constants.COMMERCIAL_REGISTER_NUMBER_LENGTH_MAX,
                    t(CompanyI18N.form.validation.commercialRegisterNumberMax, {
                        max: Constants.COMMERCIAL_REGISTER_NUMBER_LENGTH_MAX
                    })
                )
                .matches(
                    COMMERCIAL_REGISTRATION_NUMBER_REGEX,
                    t(CompanyI18N.form.validation.commercialRegisterNumberInvalid)
                ),

            vatId: yup
                .string()
                .required(t(CompanyI18N.form.field.vatId))
                .min(
                    Constants.VAT_ID_LENGTH_MIN,
                    t(CompanyI18N.form.validation.vatIdMin, { min: Constants.VAT_ID_LENGTH_MIN })
                )
                .max(
                    Constants.VAT_ID_LENGTH_MAX,
                    t(CompanyI18N.form.validation.vatIdMax, { max: Constants.VAT_ID_LENGTH_MAX })
                )
                .matches(VAT_ID_REGEX, t(CompanyI18N.form.validation.vatIdInvalid)),

            erpManufacturer: yup
                .string()
                .required(t(CompanyI18N.form.validation.erpManufacturerRequired))
                .min(
                    Constants.ERP_MANUFACTURER_LENGTH_MIN,
                    t(CompanyI18N.form.validation.erpManufacturerMin, {
                        min: Constants.ERP_MANUFACTURER_LENGTH_MIN
                    })
                )
                .max(
                    Constants.ERP_MANUFACTURER_LENGTH_MAX,
                    t(CompanyI18N.form.validation.erpManufacturerMax, {
                        max: Constants.ERP_MANUFACTURER_LENGTH_MAX
                    })
                ),

            erpSoftwareName: yup
                .string()
                .required(t(CompanyI18N.form.validation.erpSoftwareNameRequired))
                .min(
                    Constants.ERP_SOFTWARE_NAME_LENGTH_MIN,
                    t(CompanyI18N.form.validation.erpSoftwareNameMin, {
                        min: Constants.ERP_SOFTWARE_NAME_LENGTH_MIN
                    })
                )
                .max(
                    Constants.ERP_SOFTWARE_NAME_LENGTH_MAX,
                    t(CompanyI18N.form.validation.erpSoftwareNameMax, {
                        max: Constants.ERP_SOFTWARE_NAME_LENGTH_MAX
                    })
                ),

            erpSoftwareVersion: yup
                .string()
                .required(t(CompanyI18N.form.validation.erpSoftwareVersionRequired))
                .min(
                    Constants.ERP_SOFTWARE_VERSION_LENGTH_MIN,
                    t(CompanyI18N.form.validation.erpSoftwareVersionMin, {
                        min: Constants.ERP_SOFTWARE_VERSION_LENGTH_MIN
                    })
                )
                .max(
                    Constants.ERP_SOFTWARE_VERSION_LENGTH_MAX,
                    t(CompanyI18N.form.validation.erpSoftwareVersionMax, {
                        max: Constants.ERP_SOFTWARE_VERSION_LENGTH_MAX
                    })
                ),

            officeHours: yup
                .string()
                .required(t(CompanyI18N.form.validation.officeHoursRequired))
                .min(
                    Constants.OFFICE_HOURS_LENGTH_MIN,
                    t(CompanyI18N.form.validation.officeHoursMin, { min: Constants.OFFICE_HOURS_LENGTH_MIN })
                )
                .max(
                    Constants.OFFICE_HOURS_LENGTH_MAX,
                    t(CompanyI18N.form.validation.officeHoursMax, { max: Constants.OFFICE_HOURS_LENGTH_MAX })
                ),

            paymentTerms: yup
                .string()
                .required(t(CompanyI18N.form.validation.paymentTermsRequired))
                .min(
                    Constants.PAYMENT_TERMS_LENGTH_MIN,
                    t(CompanyI18N.form.validation.paymentTermsMin, { min: Constants.PAYMENT_TERMS_LENGTH_MIN })
                )
                .max(
                    Constants.PAYMENT_TERMS_LENGTH_MAX,
                    t(CompanyI18N.form.validation.paymentTermsMax, { max: Constants.PAYMENT_TERMS_LENGTH_MAX })
                ),

            additionalInformation: yup
                .string()
                .optional()
                .nullable()
                .transform((value) => value || null)
                .max(
                    Constants.ADDITIONAL_INFORMATION_LENGTH_MAX,
                    t(CompanyI18N.form.validation.additionalInformationMax, {
                        max: Constants.ADDITIONAL_INFORMATION_LENGTH_MAX
                    })
                ),

            bankName: yup
                .string()
                .required(t(CompanyI18N.form.validation.bankNameRequired))
                .min(
                    Constants.BANKNAME_LENGTH_MIN,
                    t(CompanyI18N.form.validation.bankNameMin, { min: Constants.BANKNAME_LENGTH_MIN })
                )
                .max(
                    Constants.BANKNAME_LENGTH_MAX,
                    t(CompanyI18N.form.validation.bankNameMax, { max: Constants.BANKNAME_LENGTH_MAX })
                ),

            iban: yup
                .string()
                .required(t(CompanyI18N.form.validation.ibanRequired))
                .min(
                    Constants.IBAN_LENGTH_MIN,
                    t(CompanyI18N.form.validation.ibanMin, { min: Constants.IBAN_LENGTH_MIN })
                )
                .max(
                    Constants.IBAN_LENGTH_MAX,
                    t(CompanyI18N.form.validation.ibanMax, { max: Constants.IBAN_LENGTH_MAX })
                )
                .matches(IBAN_REGEX, t(CompanyI18N.form.validation.ibanInvalid)),

            bic: yup
                .string()
                .required(t(CompanyI18N.form.validation.bicRequired))
                .min(Constants.BIC_LENGTH_MIN, t(CompanyI18N.form.validation.bicMin, { min: Constants.BIC_LENGTH_MIN }))
                .max(Constants.BIC_LENGTH_MAX, t(CompanyI18N.form.validation.bicMax, { max: Constants.BIC_LENGTH_MAX }))
                .matches(BIC_REGEX, t(CompanyI18N.form.validation.bicInvalid)),

            miniliner: yup.boolean(),

            midliner: yup.boolean(),

            maxiliner: yup.boolean(),

            dieCutting: yup.boolean(),

            folderGluerMachine: yup.boolean(),

            packaging: yup.boolean(),

            hazardousGoodsQualification: yup.boolean(),

            flexoPrinting: yup.boolean(),

            hdFlexoPrinting: yup.boolean(),

            offsetPrinting: yup.boolean(),

            digitalPrinting: yup.boolean(),

            privacyPolicyAccepted: yup
                .boolean()
                .required(t(AuthI18N.login.validation.privacyPolicyRequired))
                .oneOf([true], t(AuthI18N.login.validation.privacyPolicyRequired)),

            confidentialityAccepted: yup
                .boolean()
                .required(t(AuthI18N.login.validation.confidentialityRequired))
                .oneOf([true], t(AuthI18N.login.validation.confidentialityRequired)),

            contractAndUsageAccepted: yup
                .boolean()
                .required(t(AuthI18N.login.validation.contractAndUsageRequired))
                .oneOf([true], t(AuthI18N.login.validation.contractAndUsageRequired))
        }) as yup.ObjectSchema<CompanySignUpDTO>;
    }, [t, legalFormsByCountry, availableLanguages]);
};
